import { useState } from "react";
import { faqList } from "@/mocks/manga";
import { siteInfo } from "@/mocks/manga";

const FORM_URL = "https://readdy.ai/api/form/da25hsdv7gurs51ef7fg";

type FormState = "idle" | "sending" | "success" | "error";

export default function FAQ() {
  const [openIdx, setOpenIdx] = useState<number | null>(0);
  const [state, setState] = useState<FormState>("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    const honeypot = String(data.get("website_alt") ?? "").trim();
    if (honeypot) {
      setState("success");
      form.reset();
      return;
    }

    data.delete("website_alt");

    setState("sending");
    setFormError("");
    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(data as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const meta = (parsed?.meta ?? {}) as Record<string, unknown>;
      const serverMsg =
        (typeof meta.message === "string" && meta.message) ||
        (typeof parsed?.message === "string" && (parsed.message as string)) ||
        (typeof meta.detail === "string" && meta.detail) ||
        responseText;
      const spamHit =
        typeof serverMsg === "string" &&
        (serverMsg.toLowerCase().includes("spam") || serverMsg.includes("form data is spam"));
      const ok =
        res.ok && parsed?.code === "OK" && !spamHit && !String(serverMsg).includes("InvalidParameter");

      if (ok) {
        setState("success");
        form.reset();
      } else {
        setState("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg
            ? serverMsg
            : "提交失败，请稍后再试或直接联系我们。",
        );
      }
    } catch {
      setState("error");
      setFormError("网络异常，提交未成功，请检查网络后重试。");
    }
  };

  return (
    <section id="faq" className="scroll-mt-24 bg-background-50 py-12 md:py-16">
      <div className="mx-auto max-w-6xl px-4 md:px-6">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-5">
          <div className="lg:col-span-3">
            <p className="text-xs font-semibold tracking-widest text-primary-600">FAQ</p>
            <h2 className="mt-2 font-heading text-2xl text-foreground-950 md:text-3xl">常见问题</h2>
            <p className="mt-2 max-w-xl text-sm text-foreground-600">
              关于老漫画补档、绝版资源与使用方式，你可能想问的都在这里。
            </p>

            <div className="mt-6 space-y-3">
              {faqList.map((item, idx) => {
                const open = openIdx === idx;
                return (
                  <div
                    key={item.q}
                    className="overflow-hidden rounded-lg border border-background-200 bg-background-100"
                  >
                    <button
                      type="button"
                      onClick={() => setOpenIdx(open ? null : idx)}
                      aria-expanded={open}
                      className="flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left"
                    >
                      <h3 className="font-heading text-sm text-foreground-950 md:text-base">
                        {item.q}
                      </h3>
                      <i
                        className={`ri-arrow-down-s-line text-xl text-foreground-500 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
                      ></i>
                    </button>
                    {open && (
                      <p className="border-t border-background-200 px-4 py-3.5 text-sm leading-relaxed text-foreground-600">
                        {item.a}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-2">
            <div
              id="request"
              className="scroll-mt-24 rounded-lg border border-background-200 bg-background-100 p-6"
            >
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-500 text-background-50">
                <i className="ri-search-line text-xl"></i>
              </span>
              <h3 className="mt-4 font-heading text-lg text-foreground-950">求补一个旧坑</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                记不清名字的童年漫画？把剧情、角色和年代特征告诉我们，我们会尽力帮你寻回并整理入库。
              </p>

              <form
                data-readdy-form="073-manga-request"
                className="mt-5 space-y-4"
                onSubmit={handleSubmit}
                noValidate
              >
                <div>
                  <label htmlFor="req-title" className="mb-1.5 block text-sm text-foreground-700">
                    漫画名称或线索 <span className="text-secondary-500">*</span>
                  </label>
                  <input
                    id="req-title"
                    name="title"
                    type="text"
                    required
                    placeholder="例如：小时候看过的一部机器人漫画…"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                  />
                </div>
                <div>
                  <label htmlFor="req-desc" className="mb-1.5 block text-sm text-foreground-700">
                    剧情与角色描述
                  </label>
                  <textarea
                    id="req-desc"
                    name="detail"
                    rows={4}
                    maxLength={500}
                    placeholder="简单描述一下你记得的剧情、主角和大概的年代（最多 500 字）"
                    className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                  ></textarea>
                </div>
                <div>
                  <label htmlFor="req-email" className="mb-1.5 block text-sm text-foreground-700">
                    联系邮箱
                  </label>
                  <input
                    id="req-email"
                    name="email"
                    type="email"
                    placeholder="用于接收寻回结果"
                    className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                  />
                </div>

                <input
                  type="text"
                  name="website_alt"
                  className="field-honey"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                <button
                  type="submit"
                  disabled={state === "sending"}
                  className="flex w-full items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-4 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60"
                >
                  {state === "sending" ? "提交中…" : "提交补档请求"}
                </button>

                {state === "success" && (
                  <p className="flex items-start gap-2 rounded-md bg-accent-100 px-3 py-2.5 text-sm text-accent-700">
                    <i className="ri-checkbox-circle-line mt-0.5 text-base"></i>
                    已收到你的补档请求，我们整理后会尽快与你联系。
                  </p>
                )}
                {state === "error" && formError && (
                  <p className="flex items-start gap-2 rounded-md bg-secondary-100 px-3 py-2.5 text-sm text-secondary-800">
                    <i className="ri-error-warning-line mt-0.5 text-base"></i>
                    {formError}
                  </p>
                )}
              </form>

              <p className="mt-4 text-xs text-foreground-500">
                也可直接联系：<span className="text-foreground-700">{siteInfo.email}</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}